import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Inbox, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminEnquiries() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const token = localStorage.getItem("tfs_admin_token");

  const logout = useCallback(() => {
    localStorage.removeItem("tfs_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired, please log in again");
        logout();
      } else {
        toast.error("Could not load enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [token, navigate, load]);

  const filtered = items.filter((i) => {
    if (filter !== "all" && i.type !== filter) return false;
    if (!q) return true;
    const hay = `${i.name} ${i.email} ${i.company || ""} ${i.location || ""} ${i.message || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const counts = {
    all: items.length,
    quote: items.filter((i) => i.type === "quote").length,
    contact: items.filter((i) => i.type === "contact").length,
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-border bg-surface sticky top-0 z-10">
        <div className="max-w-container mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/tfs-logo-white.png" alt="TFS" className="h-10 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-border" />
            <h1 className="hidden sm:flex items-center gap-2 font-display font-bold text-xl text-white tracking-tight">
              <Inbox size={20} className="text-brand-light" /> Enquiries
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-sm text-slatetxt hover:text-white border border-border px-4 py-2 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-white bg-brand hover:bg-brand-hover px-4 py-2 transition-colors">
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-container mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            {[
              ["all", `All (${counts.all})`],
              ["quote", `Quotes (${counts.quote})`],
              ["contact", `Contact (${counts.contact})`],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`admin-filter-${key}`}
                className={`text-sm px-4 py-2 border transition-colors ${
                  filter === key ? "bg-brand border-brand text-white" : "bg-surface border-border text-slatetxt hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slatetxt" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-search"
              placeholder="Search name, email, company…"
              className="bg-surface border border-border text-white placeholder:text-slatetxt/60 pl-9 pr-4 py-2 text-sm outline-none focus:border-brand w-full md:w-80"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slatetxt">
            <Loader2 className="animate-spin mr-2" /> Loading enquiries…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-slatetxt border border-border bg-surface">
            <Inbox size={40} className="mx-auto mb-4 text-brand-light" />
            No enquiries yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {filtered.map((e) => (
              <div key={e.id} className="bg-surface border border-border p-6 hover:border-brand/40 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display font-semibold text-lg text-white">{e.name}</h3>
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${e.type === "quote" ? "bg-brand/20 text-brand-light" : "bg-white/10 text-slatetxt"}`}>
                        {e.type}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slatetxt">
                      <a href={`mailto:${e.email}`} className="hover:text-brand-light">{e.email}</a>
                      {e.phone && <a href={`tel:${e.phone}`} className="hover:text-brand-light">{e.phone}</a>}
                      {e.company && <span>{e.company}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-slatetxt">{fmtDate(e.created_at)}</div>
                </div>

                {(e.sector || e.location || (e.services && e.services.length > 0)) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {e.sector && <span className="text-xs border border-border px-3 py-1 text-white/80">Sector: {e.sector}</span>}
                    {e.location && <span className="text-xs border border-border px-3 py-1 text-white/80">Location: {e.location}</span>}
                    {e.services?.map((s) => (
                      <span key={s} className="text-xs border border-brand/40 text-brand-light px-3 py-1">{s}</span>
                    ))}
                  </div>
                )}

                {e.message && <p className="mt-4 text-white/85 leading-relaxed border-l-2 border-border pl-4">{e.message}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
